import { ReactNode, useEffect } from "react";
import { SidebarProvider, SidebarTrigger, SidebarInset } from "@/components/ui/sidebar";
import { AppSidebar } from "./AppSidebar";
import TopHeader from "./TopHeader";
import { MobileBottomNavigation } from "./MobileBottomNavigation";
import { OfflineBanner, CompactOfflineStatus } from "./OfflineIndicator";

import { useAuth } from "@/contexts/AuthContext"; // 1. Importamos nosso hook de autenticação
import { useNavigate } from "react-router-dom";
import { Button } from "./ui/button";
import { useBirthdayNotifications } from "@/hooks/useBirthdayNotifications";

interface LayoutProps {
  children: ReactNode;
}

const Layout = ({ children }: LayoutProps) => {
  // 2. Usamos o hook para pegar os dados REAIS e o estado de carregamento
  const { user, profile, loading, logout } = useAuth();
  const navigate = useNavigate();
  
  // Inicializa o serviço de notificações de aniversário
  useBirthdayNotifications();

  // 3. Lógica de proteção de rota: se não houver usuário, volta para a tela de login
  useEffect(() => {
    if (!loading && !user) {
      navigate('/auth');
    }
  }, [user, loading, navigate]);

  // 4. Mostra uma tela de 'carregando' enquanto o Supabase verifica a sessão do usuário
  if (loading || !profile) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <p>Carregando sistema...</p>
      </div>
    );
  }

  // 5. Lógica para o fluxo de aprovação que planejamos
  if (profile.status === 'PENDING') {
    return (
      <div className="flex h-screen items-center justify-center bg-background flex-col gap-4 p-4 text-center">
        <h1 className="text-2xl font-bold">Conta em Análise</h1>
        <p className="text-muted-foreground">Sua conta foi criada com sucesso e está aguardando a aprovação do administrador.</p>
        <Button onClick={logout} variant="outline">Sair</Button>
      </div>
    );
  }

  // 6. Se tudo estiver certo (usuário logado e ativo), renderiza o layout principal com os dados REAIS
  return (
    <SidebarProvider defaultOpen={false}>
      {/* Banner offline */}
      <OfflineBanner />
      
      <div className="min-h-screen ios-full-height flex w-full bg-background ios-safe-area-all overflow-x-hidden">
        {/* App Sidebar agora usa dados reais do perfil */}
        <AppSidebar 
          userRole={profile.role}
          userName={profile.full_name || user.email!}
        />
        
        {/* Main Content */}
        <SidebarInset>
          {/* Top Header agora usa dados reais */}
          <header className="ios-sticky-fix top-0 z-50 flex h-14 xs:h-12 sm:h-14 md:h-16 shrink-0 items-center gap-2 border-b border-border bg-background/95 backdrop-blur-md supports-[backdrop-filter]:bg-background/80 shadow-sm ios-header pwa-header">
            <div className="flex items-center gap-1 xs:gap-2 sm:gap-4 px-2 xs:px-3 sm:px-6 w-full">
              <SidebarTrigger className="text-foreground hover:bg-muted transition-fast ios-touch-target" />
              <TopHeader 
                userName={profile.full_name || user.email!}
                userRole={profile.role}
                userEmail={user.email!}
              />
              {/* Status de conexão compacto */}
              <CompactOfflineStatus className="ml-auto" />
            </div>
          </header>
          
          {/* Page Content */}
          <div className="flex flex-1 flex-col relative ios-no-bounce">
            <main className="mobile-content relative z-10 min-w-0 w-full flex-1 p-2 xs:p-3 sm:p-4 md:p-6">
              <div className="mx-auto w-full min-w-0 max-w-7xl">
                {children}
              </div>
            </main>
          </div>
        </SidebarInset>
        
        {/* Mobile Bottom Navigation */}
        <MobileBottomNavigation userRole={profile.role} />
      </div>
      

    </SidebarProvider>
  );
};

export default Layout;